<script setup lang="ts">
import type { UrlMetadata } from "ls/features/disko/api/urlMetadata.types";
import type { InlineCardNodeViewProps } from "../types";
import type { AxureMetadataMatch } from "./axure";
import SimpleButton from "ls/components/simple-button/SimpleButton.vue";
import SimpleTooltip from "ls/components/simple-tooltip/SimpleTooltip.vue";
import { useIsEmbed } from "ls/composables/useIsEmbed";
import FileIcon from "ls/features/files/FileIcon.vue";
import { useFile } from "ls/features/files/useFile";
import { useFilePages } from "ls/features/files/useFilePages";
import { isRpProjectType } from "ls/features/files/utils";
import { screenParameterName } from "ls/router/constants";
import { CircleArrowRight } from "lucide-vue-next";
import { withoutBase } from "ufo";
import { computed } from "vue";
import { useRouter } from "vue-router";
import { getRouteParam } from "@/router/utils";

const props = defineProps<InlineCardNodeViewProps & {
  match: AxureMetadataMatch;
  metadata?: UrlMetadata;
  blockCard?: boolean;
}>();

const label = computed(() => {
  if (props.node.attrs.text) return props.node.attrs.text;
  if (props.metadata && props.metadata.meta?.title) {
    return `${props.metadata.meta.title}`;
  }
  return `${props.match.shortcut}`;
});

const file = useFile(() => props.match.shortcut);
const pages = useFilePages(() => props.match.shortcut);

const router = useRouter();
const isEmbed = useIsEmbed();

const wellKnownRoute = computed(() => {
  const url = new URL(props.node.attrs.url);
  if (url.origin !== globalThis.location.origin) {
    return;
  }
  const relativePath = withoutBase(url.pathname + url.search + url.hash, import.meta.env.VITE_APP_BASE_SUFFIX);
  return router.resolve(relativePath);
});

const screen = computed(() => {
  if (!file.value || !isRpProjectType(file.value?.ShortcutType) || !wellKnownRoute.value) return;
  return getRouteParam(wellKnownRoute.value.params, screenParameterName);
});

const screenName = computed(() => {
  if (!file.value || !pages.value || !screen.value) return;
  return pages.value.find(p => p.Id === screen.value)?.Name;
});

defineExpose({
  label,
});
</script>

<template>
  <template v-if="file">
    <div class="relative top-0.5 mr-1 inline-flex">
      <FileIcon :file="file" class="size-4 flex-none" />
    </div>
    <span>
      <template v-if="!label || blockCard">
        <span>{{ file.Name }}</span>
        <span v-if="screenName"> - {{ screenName }}</span>
      </template>
      <template v-else>
        {{ label }}
      </template>
    </span>
    <SimpleTooltip v-if="!isEmbed" content="Go to project in current tab">
      <SimpleButton
        variant="link"
        size="auto"
        class="relative top-0.5 ml-1 inline-flex"
        :href="wellKnownRoute ? wellKnownRoute : props.node.attrs.url"
      >
        <CircleArrowRight class="size-4 flex-none" aria-hidden="true" />
        <span class="sr-only">Go to project</span>
      </SimpleButton>
    </SimpleTooltip>
  </template>
  <template v-else>
    <div class="relative top-0.5 mr-1 inline-flex">
      <component :is="match.icon" class="size-4 flex-none" />
    </div>
    <span>{{ label }}</span>
  </template>
</template>
